import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, ColumnAlign, GridConfig, GridRow, GridRowProblem } from '@inavista/components/grid';

// Service Order popup and ISC popup → Execution tab → Transactions on @inavista/components. The columns stay the page's
// Syncfusion column list (assignmentDetailGrid.columns): labels, widths, alignment, formats, default visibility and
// the cell editors (each column's `edit`, drawn by CellEditorHost) come from it, so the pricing, stock and
// coverage rules in those editors run exactly as before. Rows are the popup's own `assignmentDetails` array
// (LocalRowsGridApi, no endpoint), saved with the popup's Save.

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const PENCIL_ICON = '<path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';
const CUBES_ICON = '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />';

export const ADD_TRANSACTION_ACTION = 'add-transaction';
export const TRANSACTIONS_URL = 'page://transactions';
export const TRANSACTIONS_PERSISTENCE_KEY = 'rims.service.so-popup-transactions';
export const ISC_TRANSACTIONS_PERSISTENCE_KEY = 'rims.service.isc-popup-transactions';
/** A hidden field the page writes when a row opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const TRANSACTION_OPEN_FIELD = '__open';

const CART_ICON = '<circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /><line x1="14" y1="9" x2="14" y2="13" /><line x1="12" y1="11" x2="16" y2="11" />';

/** One button of the old command column: its title is passed to the page's onAssignmentDetailsCommandClick as before. */
export interface TransactionCommand { title: string; icon: string; materialOnly?: boolean }

/** Field service: Create Parts Order (Material only), Edit, Delete. */
export const TRANSACTION_COMMANDS: TransactionCommand[] = [
  { title: 'Create Parts Order', icon: CUBES_ICON, materialOnly: true },
  { title: 'Edit Assignment Detail', icon: PENCIL_ICON },
  { title: 'Delete Detail', icon: TRASH_ICON },
];

/** ISC: Add to Cart and Create Purchase Order (Material only) for ISC managers and supervisors, then Delete. */
export function iscTransactionCommands(canBuy: boolean): TransactionCommand[] {
  return [
    ...(canBuy ? [
      { title: 'Add to Cart', icon: CART_ICON, materialOnly: true },
      { title: 'Create Purchase Order', icon: CUBES_ICON, materialOnly: true },
    ] : []),
    { title: 'Delete Detail', icon: TRASH_ICON },
  ];
}

export interface TransactionsGridContext {
  persistenceKey: string;
  commands: TransactionCommand[];
  /** The cost type whose rows have the Material-only buttons. */
  materialCostType: string;
  /** The Syncfusion column list (assignmentDetailGrid.columns). */
  columns: any[];
  editors: Record<string, TemplateRef<unknown>>;
  editLockReason: (row: GridRow) => string | null;
  actionLockReason: () => string | null;
  addDisabledReason: () => string | null;
  validate: (row: GridRow) => GridRowProblem | null;
  /** Extra per-column background, header and cell, e.g. the ISC popup's blue ISC columns. */
  columnBackgrounds?: Record<string, string>;
}

/** The fields the grid shows: every column but the row key and the Actions command column. */
export function transactionFields(columns: any[]): any[] {
  return columns.filter(c => c.field && c.field !== 'rowId' && !c.commands);
}

/** Columns hidden and kept out of the Syncfusion column chooser: backing fields, never offered. */
// Estimated Qty is decided by the order's mode only, never by the user (it was never in the column chooser): hidden it is
// a backing column, shown it can't be hidden, so a saved layout can't override the mode.
function isBacking(c: any) {
  return c.visible === false && c.showInColumnChooser === false;
}

/** The editable cells: the columns with a cell editor, and those Syncfusion edited with its own text box. */
export function transactionEditFields(columns: any[]): { hosted: string[]; builtIn: string[] } {
  const fields = transactionFields(columns).filter(c => !isBacking(c));
  return {
    hosted: fields.filter(c => c.edit).map(c => c.field),
    builtIn: fields.filter(c => !c.edit && c.allowEditing !== false && c.field !== 'assignmentDetailId').map(c => c.field),
  };
}

function numberFormat(format: any): Intl.NumberFormatOptions | null {
  const m = typeof format === 'string' ? /^n(\d)$/.exec(format) : null;
  return m ? { minimumFractionDigits: +m[1], maximumFractionDigits: +m[1] } : null;
}

export function buildTransactionsActionIcons(ctx: TransactionsGridContext): ActionIconDef[] {
  const color = () => (ctx.actionLockReason() ? INACTIVE : BLACK);
  const label = (text: string) => () => ctx.actionLockReason() ?? text;
  // The key is the old title. As before, the Material-only buttons are hidden on other rows.
  return ctx.commands.map(c => ({
    key: c.title, label: c.title, icon: c.icon, colorFn: color, labelFn: label(c.title),
    ...(c.materialOnly ? { hiddenFn: (row: GridRow) => row['costTypeCode'] !== ctx.materialCostType } : {}),
  }));
}

export function buildTransactionsGridConfig(ctx: TransactionsGridContext): GridConfig {
  const fields = transactionFields(ctx.columns);
  const shown = fields.filter(c => !isBacking(c));
  const { hosted, builtIn } = transactionEditFields(ctx.columns);
  const numberFormats: Record<string, Intl.NumberFormatOptions> = {};
  const align: Record<string, ColumnAlign & { header?: ColumnAlign }> = {};
  for (const c of shown) {
    const nf = numberFormat(c.format);
    if (nf) numberFormats[c.field] = nf;
    const h = (c.textAlign ?? 'Left').toLowerCase() as 'left' | 'right' | 'center';
    align[c.field] = { horizontal: h, header: { horizontal: (c.headerTextAlign ?? 'Left').toLowerCase() as 'left' | 'right' | 'center' } };
  }
  const dates = shown.filter(c => c.type === 'date').map(c => c.field);
  return {
    apiBaseUrl: TRANSACTIONS_URL,
    clientSide: true,
    keyColumn: 'rowId',
    // 30, as the Syncfusion grid.
    rowHeight: 30,
    defaultColumnOrder: shown.map(c => c.field),
    hiddenByDefaultColumns: [...fields.filter(isBacking).map(c => c.field), TRANSACTION_OPEN_FIELD],
    startHiddenColumns: shown.filter(c => c.visible === false).map(c => c.field),
    // The old grid kept these out of its column chooser while showing them.
    alwaysVisibleColumns: shown.filter(c => c.showInColumnChooser === false && c.visible !== false).map(c => c.field),
    columnLabels: Object.fromEntries(shown.map(c => [c.field, c.headerText])),
    // The old header asterisks (red, styles.scss): the marker only; the columns' own rules decide on save.
    requiredColumns: shown.filter(c => ['Reference Activity', 'Quantity', 'Price', 'Item'].includes(c.headerText)).map(c => c.field),
    columnWidths: Object.fromEntries(shown.filter(c => c.width).map(c => [c.field, +c.width])),
    columnAlign: align,
    columnNumberFormats: numberFormats,
    columnTypes: Object.fromEntries(dates.map(f => [f, 'datetime' as const])),
    columnDateFormats: Object.fromEntries(dates.map(f => [f, 'd MMM, yyyy h:mm a'])),
    ...(ctx.columnBackgrounds ? {
      columnHeaderColors: Object.fromEntries(Object.entries(ctx.columnBackgrounds).map(([f, bg]) => [f, { background: bg }])),
      // Every cell of the column: the empty ones, and those not equal to a value no cell holds.
      conditionalFormats: Object.entries(ctx.columnBackgrounds).flatMap(([column, background]) => [
        { column, when: 'empty', style: { background } },
        { column, when: 'notEquals', value: '\u0000', style: { background } },
      ]),
    } : {}),
    excelFilterColumns: shown.filter(c => c.filter?.type === 'Excel').map(c => c.field),
    // Item and Description open the item, as before.
    hyperlinkColumns: ['itemCode', 'itemDescription'],
    // No Excel export: the old button never showed (its toolbar was set on the wrong object).
    enableExport: false,
    enableSavedViews: false,
    // The old toolbar: Columns, Add, Search, Clear filters. Reset layout is kept: it undoes the saved layout.
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    editMode: 'inline',
    editSaveMode: 'row',
    // A double click opens the row, as before.
    editTrigger: 'doubleClick',
    editableColumns: [...hosted, ...builtIn],
    editorTemplates: ctx.editors,
    readOnlyCellFn: row => !!ctx.editLockReason(row),
    // The Syncfusion validation rules of the columns (required, max with its message), read from the open editors.
    rowValidator: row => ctx.validate(row),
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions: [{
      key: ADD_TRANSACTION_ACTION, label: 'Add Assignment Detail', kind: 'button', icon: PLUS_ICON,
      disabled: () => !!ctx.addDisabledReason(), disabledReason: () => ctx.addDisabledReason() ?? '',
    }],
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ctx.persistenceKey,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
